import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, filter, finalize, Subject, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { SSOProviderEnum } from '../../constants/auth.constant';
import { AuthService } from '../../services/api/auth.service';
import { LoginService } from '../../services/storage/login.service';

let apiIsRefreshing = false;
const refreshTokenApiSubject: Subject<string> = new Subject<string>();

export const UNAUTHORIZED_REFRESH_TOKEN_INTERCEPTOR: HttpInterceptorFn = (req, next) => {
    if (req.url.indexOf(environment.apiServer) === -1) {
        return next(req);
    }

    const authService = inject(AuthService);
    const loginService = inject(LoginService);
    const msalService = inject(MsalService);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status !== 401) {
                return throwError(() => error);
            }

            if (req.url.includes('refreshToken')) {
                // 401 when calling refresh token API.

                if (loginService.getProviderType() === SSOProviderEnum.AzureAd) {
                    msalService.logoutRedirect({
                        postLogoutRedirectUri: '/home',
                    });
                }

                loginService.clear();
                window.location.href = '/home';
                return throwError(() => error);
            }

            if (apiIsRefreshing === false) {
                apiIsRefreshing = true;

                return authService.refreshToken().pipe(
                    finalize(() => (apiIsRefreshing = false)),
                    switchMap((response) => {
                        refreshTokenApiSubject.next(response.accessToken);
                        return next(updateLatestAccessToken(req, response.accessToken));
                    }),
                );
            }

            return refreshTokenApiSubject.pipe(
                filter((accessToken) => accessToken !== ''),
                take(1),
                switchMap((accessToken) => next(updateLatestAccessToken(req, accessToken))),
            );
        }),
    );

    function updateLatestAccessToken(req: HttpRequest<unknown>, accessToken: string): HttpRequest<unknown> {
        return req.clone({
            setHeaders: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }
};
